import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import Button from "../design/Button.jsx";
import AnimationPage from "../shared/animation-page/AnimationPage.js";
import { enqueueSnackbar } from "notistack";
import { JService } from "../api-service/ApiService.js";
import {
  oauthGreenTickIcon,
  oauthErrorIcon,
} from "../../utility-functions/assetLoader.js";
import { getOtherParamsObj } from "../../utility-functions/index.js";
import topNavLogo from "../../assets/top-nav-logo.svg"
import classes from "./Oauth.module.css"

const SearchParamKeys = {
  RedirectUri: "redirect_uri",
  ClientId: "client_id",
};

function redirectToCallback(callBackUrl) {
  let target;
  try {
    target = new URL(callBackUrl);
  } catch {
    enqueueSnackbar("Invalid redirect URL", { variant: "error" });
    return;
  }

  if (target.protocol === "http:" || target.protocol === "https:") {
    window.location.replace(callBackUrl);
    return;
  }

  let handedOff = false;
  const markHandedOff = () => {
    handedOff = true;
  };

  document.addEventListener("visibilitychange", markHandedOff);
  window.addEventListener("blur", markHandedOff);
  window.addEventListener("pagehide", markHandedOff);

  setTimeout(() => {
    document.removeEventListener("visibilitychange", markHandedOff);
    window.removeEventListener("blur", markHandedOff);
    window.removeEventListener("pagehide", markHandedOff);
    if (!handedOff && document.visibilityState === "visible") {
      enqueueSnackbar("Invalid redirect URL", { variant: "error" });
    }
  }, 1500);

  window.location.replace(callBackUrl);
}

export default function Oauth() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const userprofile = useSelector((state) => state?.account?.value?.data);

  const [showLoader, setShowLoader] = useState(false);
  const [isDeclinePendingResponse, setIsDeclinePendingResponse] =
    useState(false);
  const [isAcceptPendingResponse, setIsAcceptPendingResponse] = useState(false);
  const [isAcceptDisabled, setIsAcceptDisabled] = useState(true);
  const [isDeclineDisabled, setIsDeclineDisabled] = useState(true);
  const [data, setData] = useState(undefined);
  const [isClientIdInvalid, setIsClientIdInvalid] = useState(false);

  const otherParamsRef = useRef("");
  const customRedirectUri = useRef("");


  const handle401Response = () => {
    const message = "You are not logged in. Please sign in to continue.";
    enqueueSnackbar(message, { variant: "error"});
    localStorage.setItem( "frejun-login-redirect", JSON.stringify({ url: window.location.href, isRead: false }));
    navigate("/", { replace: true });
  }

  useEffect(() => {
    document.body.style.overflowY = "hidden";

    setShowLoader(true);
    let client_id = "";
    const decodedSearchParams = decodeURIComponent(window.location.search);
    if (decodedSearchParams && decodedSearchParams?.length > 0) {
      client_id =
        decodedSearchParams
          .substring(1)
          ?.split("&")
          ?.find((aa) => aa.includes(SearchParamKeys.ClientId))
          .split("=")
          .reverse()
          .at(0) || "";
    }

    if (!client_id || client_id?.trim()?.length === 0) {
      setShowLoader(false);
      setIsClientIdInvalid(true);
      return;
    } else {
      if (isClientIdInvalid) setIsClientIdInvalid(false);
    }

    const locUrl = new URL(window.location);
    if (
      locUrl.searchParams.size > 1 &&
      locUrl.searchParams.has(SearchParamKeys.ClientId)
    ) {
      locUrl.searchParams.delete(SearchParamKeys.ClientId);
      if (locUrl.searchParams.has(SearchParamKeys.RedirectUri)) {
        customRedirectUri.current = locUrl.searchParams.get(
          SearchParamKeys.RedirectUri
        );
        locUrl.searchParams.delete(SearchParamKeys.RedirectUri);
      }
      otherParamsRef.current = locUrl.search;
    }

    // redirect from login page : alreadyLoggedIn
    const url = `/api/v2/oauth/client-details/?client_id=${client_id}`;
    let isSuccess = false;
    JService.get(url)
      .then((res) => {
        // FIXME: request for change to {success, message, data} format
        console.log("response ", res);
        if (res === null) return;
        if (res?.error) throw new Error(res?.error || "Failed to load page");
        if (res && "success" in res && !res?.success) {
          setIsClientIdInvalid(true);
          throw new Error(res?.message || "Failed to load page");
        }
        isSuccess = true;
        setData(res.data);
        setIsAcceptDisabled(false);
        setIsDeclineDisabled(false);
      })
      .catch((error) => {
        console.log(error);
        setIsClientIdInvalid(true);
        setIsDeclineDisabled(true);
        setIsAcceptDisabled(true);
        if(error?.status === 401) {
          handle401Response();
        } else {
          const  message = error?.response?.data?.error || error?.message;
          enqueueSnackbar(message, {
            variant: "error",

          });
        }
      })
      .finally(() => {
        setShowLoader(false);
        if (isSuccess) localStorage.removeItem("frejun-login-redirect");
      });

    return () => {
      setData(null);
    };
  }, []);

  function handleAccept() {
    setIsAcceptPendingResponse(true);
    const url = `/api/v2/oauth/get-access-code/?client_id=${searchParams.get(SearchParamKeys.ClientId)}`;
    JService.get(url)
      .then((res) => {
        if (!res?.success) {
          throw new Error(
            res?.error || res?.message || res?.detail || "Accept failed"
          );
        } else {
          localStorage.removeItem("frejun-login-redirect");
          let callBackUrl = `${customRedirectUri.current || res?.callback_uri}?code=${res?.code}&email=${encodeURIComponent(userprofile?.email || "")}`;
          let message = {
            eventName: "oauth-code",
            data: {
              code: res?.code,
              email: userprofile?.email,
            },
          };
          if (otherParamsRef.current) {
            // pass other params from location url to request url
            let otherparamsString = otherParamsRef.current.replace("?", "");
            callBackUrl += `&${otherparamsString}`;
            let extraParamsObj = getOtherParamsObj(otherparamsString);

            message.data = {
              ...extraParamsObj,
              ...message.data,
            };
          }

          if (window.opener) {
            window.opener.postMessage(message, "*");
          }
          redirectToCallback(callBackUrl);
        }
      })
      .catch((err) => {
        console.error(err);
        if(err?.status === 401) {
          handle401Response();
        } else {
          localStorage.removeItem("frejun-login-redirect");
          enqueueSnackbar(err?.message, { variant: "error" })
        };
      }).finally(()=>setIsAcceptPendingResponse(false))
  }

  function handleDecline() {
    setIsDeclinePendingResponse(true);
    // for now show error and navigate to "/"
    const url =  `/api/v2/oauth/decline-permission/?client_id=${searchParams.get("client_id")}`;
    JService.get(url)
      .then((res) => {
        console.log(res, "response from decline permission");
        if (!res?.success) {
          throw new Error(
            res?.error || res?.message || res?.detail || "Decline failed"
          );
        } else {
          localStorage.removeItem("frejun-login-redirect");
          let callBackUrl = `${customRedirectUri.current || res?.callback_uri}?code=`;
          let message = {
            eventName: "oauth-code",
            data: {
              code: "",
              email: userprofile?.email,
            },
          };
          if (otherParamsRef.current) {
            // pass other params from location url to request url
            let otherparamsString = otherParamsRef.current.replace("?", "");
            callBackUrl += `&${otherparamsString}`;
            let extraParamsObj = getOtherParamsObj(otherparamsString);
            message.data = {
              ...extraParamsObj,
              ...message.data,
            };
          }

          if (window.opener) {
            window.opener.postMessage(message, "*");
          }
          redirectToCallback(callBackUrl);
        }
      })
      .catch((err) => {
        console.error(err);
        if (err?.status === 401) {
          handle401Response();
        } else {
          localStorage.removeItem("frejun-login-redirect");
          enqueueSnackbar(err?.message, { variant: "error" })
        };
      }).finally(()=>setIsDeclinePendingResponse(false))
  }

  return (
    <div className={classes.container}>
      <div className={classes.oauthNavbar}>
        <div
          style={{
            cursor:
              isAcceptPendingResponse || isDeclinePendingResponse
                ? "not-allowed"
                : "pointer",
          }}
          onClick={() => {
            if (isAcceptPendingResponse || isDeclinePendingResponse) return;
            setShowLoader(true);
            navigate("/activity-logs/calls", { replace: true });
          }}
        >
          <img src={topNavLogo} alt="logo"/>
        </div>
      </div>
      {showLoader ||
      (isAcceptDisabled && isDeclineDisabled && !isClientIdInvalid) ? (
        <AnimationPage height={"100px"}/>
      ) : !isClientIdInvalid ? (
        // oauth card
        <div className={classes.containerCard}>
          <div className={classes.containerHeader}>
            <div className={classes.headerLogoIconContainer}>
              <img src="/logo.svg" alt="logo"/>
            </div>
          </div>

          <div className={classes.containerBody}>
            <p            >
              <strong>'{data?.app_name?.toUpperCase()}'</strong> would like to:
            </p>

            <p
              className={classes.textPermission}
            >
              {oauthGreenTickIcon()}
              Access user and account information.
            </p>
            <p
              className={classes.textPermission}
            >
              {oauthGreenTickIcon()}
              Access information about your calls.
            </p>
          </div>

          <div className={classes.containerButtons}>
            <Button
              buttonText="Decline"
              variant="outlined"
              hierarchy="white"
              disabled={isDeclineDisabled}
              isLoading={isDeclinePendingResponse}
              onClick={handleDecline}
            />
            <Button
              buttonText="Accept"
              variant="filled"
              disabled={isAcceptDisabled}
              isLoading={isAcceptPendingResponse}
              hierarchy="green"
              onClick={handleAccept}
            />
          </div>
        </div>
      ) : (
        <div className={classes.errorContainer}>
          <span>{oauthErrorIcon()}</span>
          <h4>
            Invalid client id
          </h4>
          <p>
            Please contact app developer for more information.
          </p>
        </div>
      )}
    </div>
  );
}